import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import useOrder, { useOrderForm } from '@hooks/useOrder'

import { confirmDelete } from '@functions/entity'

const MODAL_KEY = 'UnapproveOrderConfirmationModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const customRequiredFields = [
  { key: 'data.unapprove_reason' },
]

const UnapproveOrderConfirmationModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { order: initOrder } = modalPayload

  const {
    callbacks: {
      unapproveOrder: unapproveFn,
    },
    order,
    updating,
  } = useOrder(initOrder)

  const useOrderFormPayload = useOrderForm(order, { customRequiredFields, validateOn: 'change' })
  const {
    entityState,
    entityState: {
      data: {
        unapprove_reason,
      },
    },
    errors,
    handlers,
    saveEnabled,
  } = useOrderFormPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Unapprove" titleSecondLine="Order" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Unapprove Reason" errorMessage={errors.unapprove_reason}>
            <textarea
              name="data.unapprove_reason"
              style={{ height: 160, resize: 'vertical' }}
              // @ts-expect-error TS2322
              type="text"
              value={unapprove_reason}
              {...handlers}
            />
          </Form.Field>

          <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarNoticeBox.Title>
              <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />

              <Text color="orange" style={{ display: 'inline-block' }}>&nbsp;Unapproval Warning Notice
              </Text>
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              Unapproving this order does not notify your print provider.
              By proceeding, you confirm that you have permission from the provider to do so
              and that you will be fully liable for any costs incurred if the order has
              already been completed or printed.
            </SidebarNoticeBox.Paragraph>

            <SidebarNoticeBox.Paragraph>
              If you are unsure of the order&apos;s status, contact your provider immediately.
              By clicking Unapprove, you agree that Realhub is not responsible for any financial losses
              or damages related to this action.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          onClick={() => confirmDelete({
            callbacks: {
              afterAction: closeModal,
              // @ts-expect-error TS2322
              deleteFn: unapproveFn,
            },
            options: {
              entityParams: entityState,
            },
            strings: {
              confirmString: 'Are you sure? All items in the order will be unapproved.',
              deleteString: 'unapprove',
              entityName: 'Order',
            },
          })}
          disabled={!saveEnabled}
          loading={updating}
          size="large"
        >
          Unapprove Order
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <UnapproveOrderConfirmationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
